package com.merkost.handpass.sharedweb.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.merkost.handpass.sharedweb.theme.AnimNames
import com.merkost.handpass.sharedweb.theme.Dur
import com.merkost.handpass.sharedweb.theme.Ease
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.LayoutModalWidth
import com.merkost.handpass.sharedweb.theme.Overlay
import com.merkost.handpass.sharedweb.theme.RadiusCard
import com.merkost.handpass.sharedweb.theme.Shadow
import com.merkost.handpass.sharedweb.theme.Surface
import com.merkost.handpass.sharedweb.theme.WebSize
import com.merkost.handpass.sharedweb.theme.ZModal
import com.varabyte.kobweb.compose.css.Animation
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import org.jetbrains.compose.web.css.AnimationFillMode
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

val WebModalBackdropStyle = CssStyle.base {
    Modifier
        .position(Position.Fixed)
        .styleModifier { property("inset", "0") }
        .backgroundColor(Overlay.value())
        .display(DisplayStyle.Flex)
        .justifyContent(JustifyContent.Center)
        .alignItems(AlignItems.Center)
        .styleModifier { property("z-index", ZModal.value().toString()) }
        .animation(
            Animation.of(
                name = AnimNames.BackdropFade,
                duration = Dur.Normal,
                timingFunction = Ease.Standard,
                iterationCount = AnimationIterationCount.of(1),
                fillMode = AnimationFillMode.Forwards,
            )
        )
}

val WebModalDialogStyle = CssStyle.base {
    Modifier
        .backgroundColor(Surface.value())
        .borderRadius(RadiusCard.value())
        .padding(HandpassSpacing.XL)
        .styleModifier {
            property("box-shadow", Shadow.Modal)
            property("box-sizing", "border-box")
            property("width", "min(${LayoutModalWidth.value()}, calc(100vw - ${WebSize.P32.value()}))")
        }
        .maxWidth(LayoutModalWidth.value())
        .maxHeight(80.percent)
        .overflow { y(Overflow.Auto) }
        .animation(
            Animation.of(
                name = AnimNames.ModalIn,
                duration = Dur.Normal,
                timingFunction = Ease.Standard,
                iterationCount = AnimationIterationCount.of(1),
                fillMode = AnimationFillMode.Forwards,
            )
        )
}

@Composable
fun WebModal(onDismiss: () -> Unit, ariaLabel: String? = null, content: @Composable () -> Unit) {
    var dialogEl by remember { mutableStateOf<HTMLElement?>(null) }
    WebDialogFocusScope(container = dialogEl, onDismiss = onDismiss)

    Div(
        attrs = WebModalBackdropStyle.toModifier().toAttrs {
            onClick { onDismiss() }
        }
    ) {
        Div(
            attrs = WebModalDialogStyle.toModifier().toAttrs {
                attr("role", "dialog")
                attr("aria-modal", "true")
                attr("tabindex", "-1")
                attr("aria-label", ariaLabel ?: "Dialog")
                ref { element ->
                    dialogEl = element
                    onDispose { dialogEl = null }
                }
                onClick { it.stopPropagation() }
            }
        ) {
            content()
        }
    }
}
